import { Box, Button, Chip, FormControlLabel, LinearProgress, Stack, Switch, Typography } from "@mui/material";
import React, { useState } from "react";
import Modal, { CommonModalProps } from "..";
import MetadataListTextItem from "../../Misc/Metadata/MetadataListTextItem/metadata-list-text-item";
import { DbWorkflowInstanceInfos } from "../../../Core/Api/Types/db-workflow-instance-infos";
import { workflowService } from "../../../Core/Api/services";
import DataTable, { CellSize } from "../../Table/table";
import { GridColDef, GridRowsProp } from "@mui/x-data-grid";
import moment from "moment";
import { WorkflowStatus } from "../../../Core/Api/Types/workflow-status";
import InfoIcon from "@mui/icons-material/Info";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import UpcomingIcon from "@mui/icons-material/Upcoming";
import { WorkflowActivity } from "../../../Core/Api/Types/workflow-activity";
import { AclMatch, UserHasAcl, UserHasAcls } from "../../../Utils/helpers";
import { AclTypes } from "../../../Core/Api/Types/acl-types";
import NotesIcon from "@mui/icons-material/Notes";
import { WorkflowActivityLog } from "../../../Core/Api/Types/workflow-activity-log";
import FormSection from "../../Forms/FormSection/form-section";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { useTranslation } from "react-i18next";
import { WorkflowEngine } from "../../../Core/Api/Types/workflow-engine";
import { DbKalikoWorkflowExecInfos } from "../../../Core/Api/Types/kaliko";
import SettingsDiagramDesigner from "../../../Pages/admin/Settings/SettingsDiagramDesigner/settings-diagram-designer";

export type ModalWorkflowDetailsElementProps = {
  onClose?: (props: any) => void;
  workflow: DbWorkflowInstanceInfos;
  onRetryCallback?: (workflow: DbWorkflowInstanceInfos) => void;
  onDeleteCallback?: (workflow: DbWorkflowInstanceInfos) => void;
  onAbortCallback?: (workflow: DbWorkflowInstanceInfos) => void;
  onResyncCallback?: (workflow: DbWorkflowInstanceInfos) => void;
};

export type ModalWorkflowDetailsProps = CommonModalProps<ModalWorkflowDetailsElementProps>;

const ModalWorkflowDetails = React.forwardRef<any, ModalWorkflowDetailsProps>((props, ref) => {
  const { t } = useTranslation();
  const { closeModal, onClose, ...elementProps } = props;
  const { workflow } = elementProps;
  const [inboundVars, setInboundVars] = React.useState<Record<string, string> | undefined>(undefined);
  const [filterError, setFilterError] = React.useState<boolean>(false);

  const [kalikoWorkflowExec, setKalikoWorkflowExec] = useState<DbKalikoWorkflowExecInfos | null>(null);
  const customSnackbar = useCustomSnackbar();

  React.useEffect(() => {
    if (!workflow) {
      customSnackbar(t("alert:error.workflowUndefined"), "error");
      closeModal();
      return;
    }

    workflowService.GetInstanceById(workflow.id).then((response) => {
      if (response.instance?.inboundVars !== null && response.instance?.inboundVars !== undefined)
        setInboundVars(response.instance?.inboundVars);
    });

    if (workflow.engine && workflow.engine === WorkflowEngine.Kaliko && workflow.engineId) {
      workflowService.GetKalikoBPMWorkflowExecByGuid(workflow.engineId).then((response) => {
        if (response.exec) setKalikoWorkflowExec(response.exec);
      });
    }
  }, [workflow]);

  const resync = () => {
    if (!workflow) return;

    workflowService.NotifyResync(workflow.id).then(() => {
      customSnackbar(t("alert:success.workflowResync"), "success");
      closeModal();
    });
  };

  // Activities table
  const activitieRows: GridRowsProp = workflow ? (workflow.currentActivities as WorkflowActivity[]) : [];
  type activitiesRow = (typeof activitieRows)[number];
  const activitieColumns: GridColDef[] = [
    {
      field: "type",
      headerName: t("table:headerName.taskType"),
      minWidth: CellSize.medium,
      flex: CellSize.medium,
      renderCell: (data: activitiesRow) => {
        return (
          <>
            {data.value === "servicetask" && <Chip label={t("common:process")} />}
            {data.value === "usertask" && <Chip color={"primary"} label={t("common:taskType")} />}
            {(data.value === "scripttask" || data.value === "activitycall") && <Chip label={t("common:taskType")} />}
          </>
        );
      },
    },
    {
      field: "name",
      headerName: t("common:name"),
      minWidth: CellSize.large,
      flex: CellSize.large,
      renderCell: (data: activitiesRow) => {
        return <Typography>{data.value}</Typography>;
      },
    },
    {
      field: "status",
      headerName: t("common:status"),
      minWidth: CellSize.medium,
      flex: CellSize.medium,
      renderCell: (data: activitiesRow) => {
        return (
          <>
            {data.value === WorkflowStatus.READY && <Chip label={t("common:ready")} color="secondary" />}
            {data.value === WorkflowStatus.CREATED && <Chip label={t("common:created")} color="secondary" />}
            {data.value === WorkflowStatus.RUNNING && <Chip label={t("common:running")} color="primary" />}
            {data.value === WorkflowStatus.CANCELLED && <Chip label={t("common:cancelled")} color="warning" />}
            {data.value === WorkflowStatus.ABORTED && <Chip label={t("common:aborted")} color="error" />}
            {data.value === WorkflowStatus.FAILED && <Chip label={t("common:failed")} color="error" />}
            {data.value === WorkflowStatus.COMPLETED && <Chip label={t("common:completed")} color="success" />}
          </>
        );
      },
    },
    {
      field: "progress",
      headerName: t("common:progress"),
      minWidth: CellSize.medium,
      flex: CellSize.medium,
      renderCell: (data: activitiesRow) => {
        return (
          <LinearProgress
            color={
              data?.row?.status === WorkflowStatus.RUNNING
                ? "primary"
                : data?.row?.status === WorkflowStatus.COMPLETED
                ? "success"
                : data?.row?.status === WorkflowStatus.CANCELLED
                ? "warning"
                : data?.row?.status === WorkflowStatus.ABORTED || data?.row?.status === WorkflowStatus.FAILED
                ? "error"
                : "secondary"
            }
            variant="determinate"
            value={data.value}
            sx={{ width: 100, float: "right" }}
          />
        );
      },
    },
    {
      field: "startTime",
      headerName: t("common:startDateLowerCase"),
      minWidth: CellSize.medium,
      flex: CellSize.medium,
      renderCell: (data: activitiesRow) => {
        return (
          <>
            {!data.value && <Chip label={"-"} />}
            {data.value && (
              <Stack>
                <Typography variant="body1" fontWeight={100}>
                  {moment(data.value).format(GlobalConst.MOMENT_DATE_FORMAT_DATENOHOURS)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {moment(data.value).format(GlobalConst.MOMENT_DATE_FORMAT_HOURS)}
                </Typography>
              </Stack>
            )}
          </>
        );
      },
    },
    {
      field: "endTime",
      headerName: t("common:endDateLowerCase"),
      minWidth: CellSize.medium,
      flex: CellSize.medium,
      renderCell: (data: activitiesRow) => {
        return (
          <>
            {!data.value && <Chip label={t("common:notFinished")} />}
            {data.value && (
              <Stack>
                <Typography variant="body1" fontWeight={100}>
                  {moment(data.value).format(GlobalConst.MOMENT_DATE_FORMAT_DATENOHOURS)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {moment(data.value).format(GlobalConst.MOMENT_DATE_FORMAT_HOURS)}
                </Typography>
              </Stack>
            )}
          </>
        );
      },
    },
  ];

  // Logs table
  const logRows: GridRowsProp = workflow ? (workflow.logs as WorkflowActivityLog[]) : [];
  type logRow = (typeof logRows)[number];
  const logColumns: GridColDef[] = [
    {
      field: "date",
      headerName: t("common:date"),
      minWidth: CellSize.medium,
      flex: CellSize.medium,
      renderCell: (data: logRow) => {
        return (
          <Stack pt={1} pb={1}>
            <Typography variant="body1" fontWeight={100}>
              {moment(data.value).format(GlobalConst.MOMENT_DATE_FORMAT_DATENOHOURS)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {moment(data.value).format(GlobalConst.MOMENT_DATE_FORMAT_HOURS)}
            </Typography>
          </Stack>
        );
      },
    },
    {
      field: "log",
      headerName: t("table:headerName.logMessage"),
      minWidth: CellSize.xxlarge,
      flex: CellSize.xxlarge,
      renderCell: (data: logRow) => {
        return (
          <Typography sx={{ whiteSpace: "initial" }} variant="body2">
            {data.value}
          </Typography>
        );
      },
    },
  ];

  return (
    <>
      {workflow ? (
        <Modal
          title={t("modal:title.workflowDetails") + workflow.id + " - " + workflow.definitionType}
          closeModal={closeModal}
          width={1000}
          reference={ref}
          footer={
            <Stack direction="row" justifyContent="flex-end" spacing={1}>
              {props.onAbortCallback && (
                <Button
                  color="error"
                  disabled={
                    !UserHasAcls([AclTypes.aclSuperUser]) ||
                    (workflow.status !== WorkflowStatus.CREATED && workflow.status !== WorkflowStatus.RUNNING)
                  }
                  onClick={() => {
                    if (props.onAbortCallback) props.onAbortCallback(workflow);
                  }}
                >
                  {t("common:abort")}
                </Button>
              )}

              {props.onDeleteCallback && (
                <Button
                  disabled={
                    !UserHasAcls([AclTypes.aclSuperUser]) ||
                    (workflow.status !== WorkflowStatus.READY &&
                      workflow.status !== WorkflowStatus.FAILED &&
                      workflow.status !== WorkflowStatus.ABORTED &&
                      workflow.status !== WorkflowStatus.CANCELLED)
                  }
                  onClick={() => {
                    if (props.onDeleteCallback) props.onDeleteCallback(workflow);
                  }}
                >
                  {t("common:delete")}
                </Button>
              )}

              {props.onRetryCallback && (
                <Button
                  color="success"
                  disabled={
                    !UserHasAcls([AclTypes.aclSuperUser, AclTypes.aclBackofficeWorkflowAdmin], AclMatch.OneAtLeast) ||
                    workflow.status !== WorkflowStatus.FAILED
                  }
                  onClick={() => {
                    if (props.onRetryCallback) props.onRetryCallback(workflow);
                  }}
                >
                  {t("common:retry")}
                </Button>
              )}

              {UserHasAcl(AclTypes.aclSuperUser) && (
                <Button
                  onClick={() => {
                    if (props.onResyncCallback) props.onResyncCallback(workflow);
                    else resync();
                  }}
                >
                  {t("common:resync")}
                </Button>
              )}

              <Button variant="outlined" onClick={closeModal}>
                {t("common:close")}
              </Button>
            </Stack>
          }
        >
          <Box>
            <FormSection defaultExpanded={true} title={t("common:informations")} icon={<InfoIcon />} sx={{ mb: 2 }}>
              <Stack spacing={1}>
                <MetadataListTextItem title={t("common:type")}>{workflow.definitionType || "-"}</MetadataListTextItem>
                <MetadataListTextItem title={t("common:progress")}>
                  <LinearProgress
                    color={
                      workflow.status === WorkflowStatus.RUNNING
                        ? "primary"
                        : workflow.status === WorkflowStatus.COMPLETED
                        ? "success"
                        : workflow.status === WorkflowStatus.CANCELLED
                        ? "warning"
                        : workflow.status === WorkflowStatus.ABORTED || workflow.status === WorkflowStatus.FAILED
                        ? "error"
                        : "secondary"
                    }
                    variant="determinate"
                    value={workflow.progress as number}
                    sx={{ width: 150, marginLeft: "10px" }}
                  />
                </MetadataListTextItem>
                <MetadataListTextItem title={t("common:owner")}>{workflow.createdByUserName}</MetadataListTextItem>
                {workflow.creationInfos && workflow.creationInfos.date && (
                  <MetadataListTextItem title={t("common:creationDate")}>
                    <Typography variant="body1" fontWeight={100}>
                      {moment(workflow.creationInfos.date).format(GlobalConst.MOMENT_DATE_FORMAT_DATENOHOURS)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {moment(workflow.creationInfos.date).format(GlobalConst.MOMENT_DATE_FORMAT_HOURS)}
                    </Typography>
                  </MetadataListTextItem>
                )}

                <MetadataListTextItem title={t("common:startDate")}>
                  {workflow.status === WorkflowStatus.CREATED || workflow.status == WorkflowStatus.READY ? (
                    t("common:notStarted")
                  ) : (
                    <>
                      <Typography variant="body1" fontWeight={100}>
                        {moment(workflow.startTime).format(GlobalConst.MOMENT_DATE_FORMAT_DATENOHOURS)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {moment(workflow.startTime).format(GlobalConst.MOMENT_DATE_FORMAT_HOURS)}
                      </Typography>
                    </>
                  )}
                </MetadataListTextItem>
                <MetadataListTextItem title={t("common:endDate")}>
                  {workflow.status === WorkflowStatus.RUNNING ? (
                    t("common:notFinished")
                  ) : workflow.endTime === null ? (
                    t("common:notFinished")
                  ) : (
                    <>
                      <Typography variant="body1" fontWeight={100}>
                        {moment(workflow.endTime).format(GlobalConst.MOMENT_DATE_FORMAT_DATENOHOURS)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {moment(workflow.endTime).format(GlobalConst.MOMENT_DATE_FORMAT_HOURS)}
                      </Typography>
                    </>
                  )}
                </MetadataListTextItem>
                <MetadataListTextItem title={t("common:annotation")}>
                  {workflow.annotation == null || workflow.annotation == "" ? "-" : workflow.annotation}
                </MetadataListTextItem>
                <MetadataListTextItem title={t("common:status")}>
                  {workflow.status === WorkflowStatus.ABORTED && <Chip label={t("common:aborted")} color="error" />}
                  {workflow.status === WorkflowStatus.FAILED && <Chip label={t("common:failed")} color="error" />}
                  {workflow.status === WorkflowStatus.RUNNING && <Chip label={t("common:running")} color="primary" />}
                  {workflow.status === WorkflowStatus.CANCELLED && (
                    <Chip label={t("common:cancelled")} color="warning" />
                  )}
                  {workflow.status === WorkflowStatus.COMPLETED && (
                    <Chip label={t("common:completed")} color="success" />
                  )}
                  {workflow.status === WorkflowStatus.CREATED && <Chip label={t("common:created")} color="secondary" />}
                  {workflow.status === WorkflowStatus.READY && <Chip label={t("common:ready")} color="secondary" />}
                </MetadataListTextItem>
              </Stack>
            </FormSection>

            <FormSection
              defaultExpanded={false}
              title={t("form:section.title.workflowActivitiesAndSteps")}
              icon={<AccountTreeIcon />}
              sx={{ mb: 2 }}
            >
              {(workflow.currentActivities === null || workflow.currentActivities.length === 0) && (
                <Typography component="span" fontWeight={500} color="text.tier">
                  {t("modal:modalWorkflowDetails.body.noHistoryForThisWorkflow")}
                </Typography>
              )}
              {workflow.currentActivities !== null && workflow.currentActivities.length > 0 && (
                <Box>
                  <DataTable
                    noControls
                    rows={workflow.currentActivities}
                    columns={activitieColumns}
                    columnHeaderHeight={48}
                    rowHeight={56}
                    getRowId={(row: any) => row.$id}
                    disableRowSelectionOnClick
                  />
                </Box>
              )}
            </FormSection>

            {UserHasAcls([AclTypes.aclSuperUser, AclTypes.aclAdmin]) && workflow.logs !== null && (
              <FormSection defaultExpanded={true} title={t("common:logs")} icon={<NotesIcon />} sx={{ mb: 2 }}>
                <FormControlLabel
                  onChange={() => setFilterError(!filterError)}
                  label={
                    <Typography variant="h6" component="span" color="text.secondary">
                      {t("field:label.filterLogsOnErrorsOnly")}
                    </Typography>
                  }
                  control={<Switch />}
                />
                <Box mt={2}>
                  <DataTable
                    noControls
                    rows={
                      filterError === false
                        ? workflow.logs
                        : workflow.logs.filter(
                            (x) =>
                              x.log !== null && (x.log.includesAllWords("failed") || x.log.includesAllWords("invalid"))
                          )
                    }
                    columns={logColumns}
                    columnHeaderHeight={48}
                    getRowHeight={() => "auto"}
                    getRowId={(row: any) => row.$id}
                    disableRowSelectionOnClick
                  />
                </Box>
              </FormSection>
            )}

            {inboundVars !== null && inboundVars !== undefined && UserHasAcl(AclTypes.aclSuperUser) && (
              <FormSection
                defaultExpanded={false}
                title={t("form:section.title.inboundVariables")}
                icon={<UpcomingIcon />}
              >
                <Stack spacing={1}>
                  {Object.keys(inboundVars).map((x, index) => (
                    <MetadataListTextItem title={x} key={index}>
                      {Object.values(inboundVars)[index]}
                    </MetadataListTextItem>
                  ))}
                </Stack>
              </FormSection>
            )}
            {kalikoWorkflowExec && (
              <FormSection
                sx={{ mt: 2 }}
                defaultExpanded={false}
                title={t("form:section.title.workflowViewer")}
                icon={<UpcomingIcon />}
              >
                <SettingsDiagramDesigner workflowId={workflow.id} exec={kalikoWorkflowExec} />
              </FormSection>
            )}
          </Box>
        </Modal>
      ) : (
        <></>
      )}
    </>
  );
});

export default ModalWorkflowDetails;
